/**
 * O conteúdo editorial da home.
 *
 * O template traz seções que a plataforma ainda não tem como alimentar sozinha:
 * depoimentos, patrocinadores, planos, notícias e a galeria da marca. Nenhuma
 * delas sai do banco — todas dependem de alguém da organização escrever.
 *
 * Elas moram todas aqui, num arquivo só, por dois motivos:
 *
 *   1. quem for preencher não precisa abrir componente nenhum, nem saber o que
 *      é JSX. Troca o texto entre aspas e pronto;
 *   2. fica evidente o que é conteúdo de verdade e o que é lugar reservado. O
 *      que está marcado como PENDENTE aparece na tela com a cara de exemplo que
 *      tem — nomes genéricos, valores redondos — em vez de inventar um capitão
 *      chamado Alex Romero elogiando uma competição que ele nunca disputou.
 *
 * Depoimento inventado é a mentira mais barata de escrever e a mais cara de
 * sustentar: a lista de times está a um clique daqui, e quem procurar o autor
 * do elogio não vai achar. Enquanto não houver depoimento real, é melhor a
 * seção anunciar que está esperando um.
 */

/* ------------------------------------------------------------------ *
 * Comunidade
 * ------------------------------------------------------------------ */

export const COMUNIDADE = {
  titulo: 'Faça parte da comunidade',
  texto:
    'Cada gol, cada virada e cada título é sustentado pela comunidade que joga. Junte-se aos capitães, elencos e torcedores que fazem da Hexa Cup mais do que uma tabela de jogos.',
  itens: [
    'Sorteio aberto e conferível',
    'Resultados e tabela ao vivo',
    'Suporte por ticket, com prazo',
    'Sua equipe no histórico da competição',
  ],
  botao: { rotulo: 'Cadastrar meu time', destino: '/cadastrar-time' },
  /**
   * Os canais oficiais, um card para cada, nesta ordem. O WhatsApp vem primeiro:
   * é onde os avisos chegam antes.
   */
  canais: [
    {
      id: 'whatsapp',
      link: 'https://chat.whatsapp.com/JYRMqx6mx3r8EwUQh1nVNV',
      titulo: 'Grupo no WhatsApp',
      texto: 'É lá que a Hexa Cup acontece entre uma rodada e outra. Os avisos chegam primeiro no grupo.',
      itens: [
        'Novas edições e inscrições abertas',
        'Horários, chaves e sorteio de cada rodada',
        'Resultados e recados da organização',
      ],
      botao: 'Entrar no grupo',
      rodape: 'Abre o WhatsApp · grátis',
    },
    {
      id: 'discord',
      link: 'https://discord.gg/UdNe2W4eT',
      titulo: 'Servidor no Discord',
      texto: 'O ponto de encontro de capitães e jogadores: conversa por assunto e voz para combinar as partidas.',
      itens: [
        'Chat com capitães e jogadores',
        'Canais de voz para combinar os jogos',
        'Contato direto com a organização',
      ],
      botao: 'Entrar no servidor',
      rodape: 'Abre o Discord · grátis',
    },
    {
      id: 'tiktok',
      link: 'https://www.tiktok.com/@..hexacup',
      titulo: 'Hexa Cup no TikTok',
      texto: 'Os gols, as viradas e os bastidores da competição em vídeo curto.',
      itens: [
        'Gols e lances das rodadas',
        'Sorteios e bastidores',
        'Chamadas das novas edições',
      ],
      botao: 'Seguir no TikTok',
      rodape: '@..hexacup',
    },
  ],
} as const;

/* ------------------------------------------------------------------ *
 * Planos — PENDENTE
 * ------------------------------------------------------------------ *
 *
 * No template esta seção vende passes de transmissão e cotas de patrocínio. Na
 * Hexa Cup o equivalente honesto são as formas de participar: a inscrição da equipe,
 * que já existe e tem preço real vindo da edição, e o patrocínio, que ainda não
 * tem tabela fechada.
 *
 * Os valores abaixo são exemplo. Enquanto a organização não fechar a tabela,
 * eles ficam com o rótulo "a combinar" em vez de um número inventado — anunciar
 * R$ 10.000 de cota e depois cobrar outra coisa é o tipo de detalhe que custa
 * um patrocinador.
 */

export interface Plano {
  nome: string;
  preco: string;
  destaque?: boolean;
  botao: { rotulo: string; destino: string };
  inclui: string[];
}

export const PLANOS: { equipes: Plano[]; patrocinio: Plano[] } = {
  equipes: [
    {
      nome: '🏅 Equipe convidada',
      preco: 'Gratuito',
      botao: { rotulo: 'Cadastrar time', destino: '/cadastrar-time' },
      inclui: [
        'Vaga em edições sem taxa de inscrição',
        'Página do time com elenco e histórico',
        'Estatísticas da EA sincronizadas',
      ],
    },
    {
      nome: '⚡ Equipe inscrita',
      preco: 'Por edição',
      destaque: true,
      botao: { rotulo: 'Ver campeonatos', destino: '/campeonatos' },
      inclui: [
        'Vaga garantida na edição escolhida',
        'Disputa de grupos e mata-mata',
        'Direito à premiação da colocação',
        'Abertura de tickets com prazo de resposta',
      ],
    },
    {
      nome: '🏆 Temporada',
      preco: 'A combinar',
      botao: { rotulo: 'Falar com a organização', destino: '/suporte' },
      inclui: [
        'Vaga em todas as edições da temporada',
        'Prioridade na fila de inscrição',
        'Destaque na página de times',
      ],
    },
  ],
  patrocinio: [
    {
      nome: '🥈 Apoio',
      preco: 'A combinar',
      botao: { rotulo: 'Falar com a organização', destino: '/suporte' },
      inclui: ['Marca no site da competição', 'Menção nas publicações da edição'],
    },
    {
      nome: '🥇 Patrocínio',
      preco: 'A combinar',
      destaque: true,
      botao: { rotulo: 'Falar com a organização', destino: '/suporte' },
      inclui: [
        'Marca no site e nas artes das partidas',
        'Menção nas transmissões',
        'Espaço na página de cada edição',
      ],
    },
    {
      nome: '💎 Cota principal',
      preco: 'A combinar',
      botao: { rotulo: 'Falar com a organização', destino: '/suporte' },
      inclui: [
        'Nome junto ao da competição',
        'Marca em todas as peças e transmissões',
        'Ação combinada com a organização',
      ],
    },
  ],
};

/* ------------------------------------------------------------------ *
 * Patrocinadores — PENDENTE
 * ------------------------------------------------------------------ *
 *
 * Vazio de propósito. A seção some da página enquanto não houver patrocinador
 * de verdade: uma faixa de logos genéricos anuncia apoio que não existe, e é a
 * primeira coisa que um patrocinador real repara quando vai avaliar a cota.
 */
export const PATROCINADORES: Array<{ nome: string; logoUrl: string; site?: string }> = [];

/* ------------------------------------------------------------------ *
 * Depoimentos — PENDENTE
 * ------------------------------------------------------------------ *
 *
 * Também vazio, e pela mesma razão. Quando existirem depoimentos reais, cada um
 * entra aqui com o nome de quem falou e a equipe — e a seção volta a aparecer.
 */
export interface Depoimento {
  texto: string;
  autor: string;
  papel: string;
  imagemUrl?: string;
}

export const DEPOIMENTOS: Depoimento[] = [];

/* ------------------------------------------------------------------ *
 * Notícias — PENDENTE
 * ------------------------------------------------------------------ *
 *
 * O template tem um blog. A Hexa Cup ainda não tem — quando tiver, isto aqui vira
 * uma consulta à API em vez de uma lista escrita à mão.
 */
export interface Noticia {
  titulo: string;
  data: string;
  leitura: string;
  imagemUrl: string;
  destino: string;
}

export const NOTICIAS: Noticia[] = [];

/* ------------------------------------------------------------------ *
 * Destaques da marca
 * ------------------------------------------------------------------ */

export const MARCA = {
  titulo: 'O grande momento da sua marca',
  texto: 'Coloque sua marca diante da comunidade de Pro Clubs que assiste a cada rodada.',
  botao: { rotulo: 'Quero patrocinar', destino: '/suporte' },
  /** Vazio até haver arte de divulgação real para mostrar. */
  imagens: [] as Array<{ url: string; alt: string }>,
};
